import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const Contact = () => {
    const navigate = useNavigate();
    const [showPopup, setShowPopup] = useState(false);
    const [popupPlatform, setPopupPlatform] = useState('');

    const handleComingSoon = (platform) => {
        setPopupPlatform(platform);
        setShowPopup(true);
    };

    const pageVariants = {
        initial: {
            y: "100vh",
            opacity: 0
        },
        animate: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.8,
                ease: [0.25, 0.46, 0.45, 0.94],
                staggerChildren: 0.1
            }
        },
        exit: {
            y: "100vh",
            opacity: 0,
            transition: {
                duration: 0.6,
                ease: [0.25, 0.46, 0.45, 0.94]
            }
        }
    };

    const contentVariants = {
        initial: {
            opacity: 0,
            y: 30
        },
        animate: {
            opacity: 1,
            y: 0,
            transition: {
                delay: 0.2,
                duration: 0.6,
                ease: [0.25, 0.46, 0.45, 0.94]
            }
        }
    };

    const handleHomeClick = () => {
        navigate('/');
    };

    return (
        <motion.div
            className="bg-[#1A1818] min-h-screen w-full relative text-white md:items-center flex overflow-hidden"
            variants={pageVariants}
            initial="initial"
            animate="animate"
            exit="exit"
        >
            {/* Left Sidebar Elements */}

            {/* HOME Vertical Text */}
            <motion.button
                onClick={handleHomeClick}
                className="fixed top-0 left-0 z-50 flex flex-col items-center group hidden md:flex"
                style={{
                    padding: '20px'
                }}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
            >
                <motion.span
                    className="text-white text-sm font-medium"
                    style={{
                        writingMode: 'vertical-rl',
                        textOrientation: 'mixed',
                        transform: 'rotate(180deg)'
                    }}
                    whileHover={{ letterSpacing: "0.2em" }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                >
                    HOME
                </motion.span>
                {/* Vertical line below HOME text */}
                <motion.div
                    className="w-[1px] bg-white"
                    initial={{ height: '80px', opacity: 0.6 }}
                    whileHover={{ height: '100px', opacity: 1 }}
                    transition={{ duration: 0.3, ease: "easeOut" }}
                    style={{
                        marginTop: '20px'
                    }}
                />
            </motion.button>

            {/* 2025 Vertical Text - Adjusted position */}
            <div className="fixed left-8 bottom-8 z-50 hidden md:block">
                <span
                    className="text-white/40 text-[10px] font-sans tracking-widest block"
                    style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                >
                    © 2025
                </span>
            </div>

            {/* Main Content */}
            <motion.div
                className="w-full max-w-7xl mx-auto px-6 md:px-20 pt-4 md:pt-20"
                variants={contentVariants}
            >
                {/* Mobile Top Bar - HOME + Line */}
                <div className="md:hidden flex items-center mb-12 w-full gap-4">
                    <button
                        onClick={() => navigate('/')}
                        className="text-white text-sm font-medium tracking-widest uppercase"
                    >
                        HOME
                    </button>
                    <div className="h-[1px] bg-white w-24 opacity-20"></div>
                </div>
                <div className="flex flex-col items-start justify-center h-full">

                    {/* Title */}
                    <h1 className="font-bonky text-[20vw] md:text-[15rem] leading-[0.8] tracking-tight mb-12 md:mb-16">
                        Hello.
                    </h1>

                    {/* Subtitle */}
                    <div className="max-w-xl md:pl-2 mb-12 md:mb-20">
                        <p className="text-white/80 font-sans text-base md:text-lg font-light leading-relaxed">
                            Need a beautiful, well-structured website that you can own and maintain yourself? Get in touch.
                        </p>
                    </div>

                    {/* Contact Info */}
                    <div className="md:pl-2 space-y-6 md:space-y-4 font-sans text-sm md:text-base font-light text-white/90">
                        <div className="flex flex-col md:flex-row gap-1 md:gap-2">
                            <span className="text-white/60">Email:</span>
                            <a href="mailto:aimanrahimi45@gmail.com" className="hover:underline underline-offset-4 decoration-white/30 transition-all text-emerald-400 md:text-white">
                                aimanrahimi45@gmail.com
                            </a>
                        </div>

                        <div className="flex flex-col md:flex-row gap-1 md:gap-2">
                            <span className="text-white/60">On the Internet:</span>
                            <div className="flex flex-wrap gap-x-2 gap-y-1">
                                {[
                                    { name: 'LinkedIn', url: 'https://www.linkedin.com/in/aiman-rahimi-a78a91137/', isComingSoon: false },
                                    { name: 'Dribbble', url: '#', isComingSoon: true },
                                    { name: 'Threads', url: 'https://www.threads.com/@aimanlati', isComingSoon: false },
                                    { name: 'Github', url: '#', isComingSoon: true }
                                ].map((link, i, arr) => (
                                    <React.Fragment key={link.name}>
                                        {link.isComingSoon ? (
                                            <button
                                                onClick={() => handleComingSoon(link.name)}
                                                className="hover:underline underline-offset-4 decoration-white/30 transition-all text-left bg-transparent border-none p-0 cursor-pointer text-white/90 hover:text-white"
                                            >
                                                {link.name}
                                            </button>
                                        ) : (
                                            <a
                                                href={link.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="hover:underline underline-offset-4 decoration-white/30 transition-all"
                                            >
                                                {link.name}
                                            </a>
                                        )}
                                        {i < arr.length - 1 && <span className="text-white/30">/</span>}
                                    </React.Fragment>
                                ))}
                            </div>
                        </div>
                    </div>

                </div>
            </motion.div>

            {/* Coming Soon Popup */}
            <AnimatePresence>
                {showPopup && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                            onClick={() => setShowPopup(false)}
                        >
                            {/* Popup */}
                            <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                                transition={{
                                    duration: 0.4,
                                    ease: [0.25, 0.46, 0.45, 0.94]
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="bg-[#2A2727] border border-white/10 rounded-2xl p-8 md:p-12 w-full max-w-[400px] shadow-2xl"
                            >
                                {/* Platform Icon/Emoji */}
                                <motion.div
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                                    className="text-5xl mb-6 text-center"
                                >
                                    {popupPlatform === 'Dribbble' ? '🏀' : '🐙'}
                                </motion.div>

                                {/* Title */}
                                <h2 className="text-white text-2xl md:text-3xl font-light mb-3 text-center tracking-wide">
                                    {popupPlatform}
                                </h2>

                                {/* Message */}
                                <p className="text-white/70 text-base text-center leading-relaxed mb-8">
                                    Coming soon<br/>
                                    <span className="text-white/50 text-sm">Stay tuned for updates</span>
                                </p>

                                {/* Close Button */}
                                <motion.button
                                    whileHover={{ scale: 1.02 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => setShowPopup(false)}
                                    className="w-full bg-white/10 hover:bg-white/15 text-white py-3 px-6 rounded-xl transition-all duration-300 border border-white/10 font-light tracking-wide"
                                >
                                    Got it
                                </motion.button>
                            </motion.div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>

        </motion.div>
    );
};

export default Contact;
